import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, Inject, Optional, OnInit, ChangeDetectorRef } from '@angular/core';
import { PageEvent, MatPaginatorIntl } from '@angular/material/paginator';

@Component({
  selector: 'c4g-paginator',
  templateUrl: './paginator.component.html',
  styleUrls: ['./paginator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class C4gPaginatorComponent implements OnInit {
  @Input() length = 0;
  @Input() pageSize = 10;
  @Input() pageSizeOptions = [5, 10, 25, 100];
  @Input() pageIndex = 0;
  @Input() showFirstLastButtons = false;
  @Input() disabled = false;
  @Input() hidePageSize = false;

  @Output() page = new EventEmitter<PageEvent>();

  private _initialized = false;

  constructor(
    @Optional() @Inject(MatPaginatorIntl) private paginatorIntl: MatPaginatorIntl,
    private cdr: ChangeDetectorRef
  ) {
    if (!this.paginatorIntl) {
      this.paginatorIntl = new MatPaginatorIntl();
    }
  }

  ngOnInit() {
    this._initialized = true;
    // Emit initial page event to ensure parent component is notified
    this.emitPageEvent(this.pageIndex, this.pageSize);
  }

  private emitPageEvent(pageIndex: number, pageSize: number) {
    console.log('[C4gPaginatorComponent] emitPageEvent', { pageIndex, pageSize, length: this.length });
    this.page.emit({
      pageIndex,
      pageSize,
      length: this.length
    });
  }

  getCount(): number {
    return this.length;
  }

  getMaxPage(): number {
    return Math.ceil(this.length / this.pageSize) - 1;
  }

  getPages(): number[] {
    const maxPage = this.getMaxPage();
    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(0, this.pageIndex - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(maxPage, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(0, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  }

  getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0 || pageSize === 0) {
      return `Page 0 of 0`;
    }
    const totalPages = Math.ceil(length / pageSize);
    return `Page ${page + 1} of ${totalPages}`;
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.cdr.markForCheck();
    this.page.emit(event);
  }

  onPageSizeChange(newPageSize: number): void {
    this.pageSize = Number(newPageSize);
    this.pageIndex = 0;
    this.cdr.markForCheck();
    this.emitPageEvent(0, this.pageSize);
  }

  getItemsPerPageLabel(): string {
    return this.paginatorIntl.itemsPerPageLabel;
  }

  getNextPageLabel(): string {
    return this.paginatorIntl.nextPageLabel;
  }

  getPreviousPageLabel(): string {
    return this.paginatorIntl.previousPageLabel;
  }

  getFirstPageLabel(): string {
    return this.paginatorIntl.firstPageLabel;
  }

  getLastPageLabel(): string {
    return this.paginatorIntl.lastPageLabel;
  }

  getPagesAll(): number[] {
    // Returns all page indices for the select dropdown
    return Array.from({ length: this.getMaxPage() + 1 }, (_, i) => i);
  }

  onGoToPageChange(pageIndex: number | string): void {
    this.pageIndex = Number(pageIndex);
    this.cdr.markForCheck();
    this.onPageChange({ pageIndex: this.pageIndex, pageSize: this.pageSize, length: this.length });
  }

  displayPageSize(value: any): string {
    return String(value);
  }

  displayGoToPage(value: any): string {
    return String(Number(value) + 1);
  }
}
